/**
 * Notification Service
 * Handles creation of flight-related notifications
 */

class NotificationService {
  constructor() {
    this.notificationContext = null;
  }

  /**
   * Set the notification context (called by NotificationProvider)
   */
  setContext(context) {
    this.notificationContext = context;
  }

  /**
   * Create a flight delay notification
   */
  notifyFlightDelay(flight, delayMinutes) {
    if (!this.notificationContext) return;

    this.notificationContext?.addNotification({
      type: 'delay',
      title: `Flight ${flight?.flight_number || 'Unknown'} Delayed`,
      message: `Delayed by ${delayMinutes} minutes. New departure: ${this.formatTime(flight?.actual_depart || flight?.scheduled_depart)}`,
      flightNumber: flight?.flight_number,
      flightId: flight?.id,
      actions: [
        {
          label: 'View Details',
          variant: 'outline',
          onClick: () => {
            window.location.href = `/rotation-detail/${flight?.rotation_id}`;
          }
        }
      ]
    });
  }

  /**
   * Create a gate change notification
   */
  notifyGateChange(flight, oldGate, newGate) {
    if (!this.notificationContext) return;

    this.notificationContext?.addNotification({
      type: 'gate_change',
      title: `Gate Change: ${flight?.flight_number || 'Unknown'}`,
      message: `Gate changed from ${oldGate || 'N/A'} to ${newGate || 'N/A'}`,
      flightNumber: flight?.flight_number,
      flightId: flight?.id,
      actions: [
        {
          label: 'View Flight',
          variant: 'outline',
          onClick: () => {
            window.location.href = `/rotation-detail/${flight?.rotation_id}`;
          }
        }
      ]
    });
  }

  /**
   * Create a flight status update notification
   */
  notifyStatusUpdate(flight, status) {
    if (!this.notificationContext) return;

    const statusMessages = {
      'scheduled': 'Flight is scheduled',
      'boarding': 'Boarding has started',
      'departed': 'Flight has departed',
      'in_air': 'Flight is in the air',
      'landed': 'Flight has landed',
      'arrived': 'Flight has arrived at gate',
      'cancelled': 'Flight has been cancelled',
      'diverted': 'Flight has been diverted'
    };

    this.notificationContext?.addNotification({
      type: status === 'cancelled' ? 'cancellation' : 'status_update',
      title: `${flight?.flight_number || 'Flight'} - ${status?.toUpperCase()}`,
      message: statusMessages?.[status] || `Status updated to ${status}`,
      flightNumber: flight?.flight_number,
      flightId: flight?.id,
      actions: [
        {
          label: 'View Details',
          variant: 'outline',
          onClick: () => {
            window.location.href = `/rotation-detail/${flight?.rotation_id}`;
          }
        }
      ]
    });
  }

  /**
   * Create a custom notification
   */
  notify({ type = 'status_update', title, message, flightNumber, flightId, actions, showToast = true }) {
    if (!this.notificationContext) return;

    this.notificationContext?.addNotification({
      type,
      title,
      message,
      flightNumber,
      flightId,
      actions,
      showToast
    });
  }

  /**
   * Format time helper
   */
  formatTime(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'N/A';
    }
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;